import { useAtomValue } from 'jotai';
import { indexingProgressByWorkspaceAtom } from '@localagent/sdk/react';
import type { IndexingProgress } from '@localagent/sdk';
import { launchedWorkspaceIdsAtom } from '../../stores/workspace/atoms';

/**
 * Derived display state for the central-tier indexing indicator.
 *
 *  - `idle`      — no progress frame seen for this workspace yet (nothing to show).
 *  - `indexing`  — chunks are queued and/or embedding; counts shown live.
 *  - `done`      — the last frame settled (queued=0, embedding=0) with some
 *                  indexed count; shows "Indexed N".
 */
export type IndexingDisplayState = 'idle' | 'indexing' | 'degraded' | 'done';

export interface IndexingIndicatorState {
  state: IndexingDisplayState;
  queued: number;
  embedding: number;
  indexed: number;
}

/**
 * Pure derivation from a workspace's latest progress frame (or undefined when
 * none has arrived). Exported so the presentational view can be exercised
 * across all states without a live bridge.
 */
export function deriveIndexingState(
  progress: IndexingProgress | undefined,
): IndexingIndicatorState {
  if (!progress) {
    return { state: 'idle', queued: 0, embedding: 0, indexed: 0 };
  }
  if (progress.health === 'degraded') {
    return { state: 'degraded', queued: progress.queued, embedding: progress.embedding, indexed: progress.indexed };
  }
  const { queued, embedding, indexed } = progress;
  // Terminal state: nothing queued and nothing embedding means the run settled.
  const state: IndexingDisplayState =
    queued === 0 && embedding === 0 ? 'done' : 'indexing';
  return { state, queued, embedding, indexed };
}

function badgeColor(state: IndexingDisplayState): string {
  switch (state) {
    case 'idle':
      return 'bg-muted-foreground/40';
    case 'indexing':
      return 'bg-amber-500 animate-pulse';
    case 'degraded':
      return 'bg-red-500';
    case 'done':
      return 'bg-green-500';
  }
}

function label(s: IndexingIndicatorState): string {
  switch (s.state) {
    case 'idle':
      return 'Search idle';
    case 'indexing': {
      const pending = s.queued + s.embedding;
      return `Indexing ${pending}`;
    }
    case 'degraded':
      return 'Search behind';
    case 'done':
      return `Indexed ${s.indexed}`;
  }
}

function title(s: IndexingIndicatorState): string {
  switch (s.state) {
    case 'idle':
      return 'No search indexing activity for this workspace';
    case 'indexing':
      return `Search indexing: ${s.queued} queued, ${s.embedding} embedding, ${s.indexed} indexed`;
    case 'degraded':
      return `Search index is behind — ${s.queued} queued, ${s.embedding} embedding, ${s.indexed} indexed`;
    case 'done':
      return `Search index up to date — ${s.indexed} chunks indexed`;
  }
}

/**
 * Presentational indicator — pure function of derived state so it can be
 * exercised across all states in a Storybook functional story without a live
 * bridge connection.
 */
export function IndexingProgressIndicatorView({
  state,
}: {
  state: IndexingIndicatorState;
}) {
  return (
    <div
      data-testid="indexing-progress-indicator"
      data-state={state.state}
      className="flex items-center gap-1.5 text-xs text-muted-foreground"
      title={title(state)}
    >
      <span className={`flex h-2 w-2 rounded-full ${badgeColor(state.state)}`} />
      <span>{label(state)}</span>
    </div>
  );
}

/**
 * Container — selects THIS workspace's latest central-tier indexing progress
 * from the SDK atom (the atom is keyed by workspaceId, so account-wide fanout
 * frames for other workspaces never show here) and renders the view.
 *
 * Renders nothing until a workspace is launched — the indicator has no meaning
 * without a current workspace.
 */
export function IndexingProgressIndicator() {
  const byWorkspace = useAtomValue(indexingProgressByWorkspaceAtom);
  const launchedIds = useAtomValue(launchedWorkspaceIdsAtom);
  const workspaceId = launchedIds[0] ?? null;

  if (!workspaceId) return null;

  const progress = byWorkspace[workspaceId];
  return <IndexingProgressIndicatorView state={deriveIndexingState(progress)} />;
}
